/**
 * 자동 생성 — 손으로 고치지 않는다. `npm run sync:devices` (scripts/sync-devices.js).
 * 원천: S3 judahs-midi-database 의 cc.json · sysex.json. 합집합은 이름을 정규화해 실제로 센 값이다.
 * 동기화: 2026-09-14
 */
export interface DeviceEntryType {
	name: string
	/** 페이지 앵커 id. 늘 제조사-장비다(neural-dsp-quad-cortex) — 장비 이름만 쓰면 나중에 겹친다 */
	slug: string
	/** CC 딕셔너리 항목 수 */
	cc: number
	/** SysEx 딕셔너리 항목 수 */
	sysex: number
}

export interface DeviceManufacturerType {
	name: string
	slug: string
	devices: DeviceEntryType[]
}

export const deviceStats = {
	/** CC + SysEx 합집합. 사이트에 보이는 "N manufacturers · N devices" 는 이 값 */
	manufacturers: 112,
	devices: 482,
	parameters: 17308,
	cc: { manufacturers: 104, devices: 453, parameters: 16165, updated: '2026-09-14' },
	sysex: { manufacturers: 18, devices: 39, parameters: 1143, updated: '2026-09-14' },
	syncedAt: '2026-09-14',
} as const

export type DeviceStats = typeof deviceStats

/** 제조사 A→Z, 장비 A→Z. Works with 페이지의 본문 */
export const deviceList: DeviceManufacturerType[] = [
	{
		name: '3 Degrees Audio',
		slug: '3-degrees-audio',
		devices: [
			{
				name: 'Hue Stereo Multi Effect Pedal',
				slug: '3-degrees-audio-hue-stereo-multi-effect-pedal',
				cc: 17,
				sysex: 0,
			},
		],
	},
	{
		name: '1010music',
		slug: '1010music',
		devices: [{ name: 'bento', slug: '1010music-bento', cc: 155, sysex: 0 }],
	},
	{
		name: 'ADA',
		slug: 'ada',
		devices: [{ name: 'MP-1', slug: 'ada-mp-1', cc: 0, sysex: 3 }],
	},
	{
		name: 'Adrenalinn',
		slug: 'adrenalinn',
		devices: [{ name: 'III', slug: 'adrenalinn-iii', cc: 10, sysex: 0 }],
	},
	{
		name: 'Aerospace Audio',
		slug: 'aerospace-audio',
		devices: [
			{ name: 'Atmosphere v2', slug: 'aerospace-audio-atmosphere-v2', cc: 51, sysex: 0 },
			{ name: 'Atmosphere v3', slug: 'aerospace-audio-atmosphere-v3', cc: 36, sysex: 0 },
		],
	},
	{
		name: 'Alesis',
		slug: 'alesis',
		devices: [{ name: 'Quadraverb GT', slug: 'alesis-quadraverb-gt', cc: 0, sysex: 2 }],
	},
	{
		name: 'Alexander',
		slug: 'alexander',
		devices: [
			{ name: 'Colour Theory ', slug: 'alexander-colour-theory', cc: 31, sysex: 0 },
			{ name: 'Defender Neo', slug: 'alexander-defender-neo', cc: 19, sysex: 0 },
			{ name: 'F.13 Neo', slug: 'alexander-f-13-neo', cc: 21, sysex: 0 },
			{ name: 'Fever Pitch', slug: 'alexander-fever-pitch', cc: 44, sysex: 0 },
			{ name: 'Luminous', slug: 'alexander-luminous', cc: 37, sysex: 0 },
			{ name: 'Marshmallow', slug: 'alexander-marshmallow', cc: 22, sysex: 0 },
			{
				name: 'Now We Are Ghosts',
				slug: 'alexander-now-we-are-ghosts',
				cc: 20,
				sysex: 0,
			},
			{
				name: 'Oblivion Vintage Delay',
				slug: 'alexander-oblivion-vintage-delay',
				cc: 24,
				sysex: 0,
			},
			{ name: 'Quadrant', slug: 'alexander-quadrant', cc: 15, sysex: 0 },
			{
				name: 'Quadrant Audio Mirror',
				slug: 'alexander-quadrant-audio-mirror',
				cc: 21,
				sysex: 0,
			},
			{ name: 'Radical Delay DX', slug: 'alexander-radical-delay-dx', cc: 19, sysex: 0 },
			{ name: 'Rewind', slug: 'alexander-rewind', cc: 16, sysex: 0 },
			{ name: 'SKY 5000', slug: 'alexander-sky-5000', cc: 24, sysex: 0 },
			{ name: 'Space Race', slug: 'alexander-space-race', cc: 16, sysex: 0 },
			{ name: 'Super Neo-Matic', slug: 'alexander-super-neo-matic', cc: 15, sysex: 0 },
			{
				name: 'Super Radical Delay',
				slug: 'alexander-super-radical-delay',
				cc: 24,
				sysex: 0,
			},
			{ name: 'Superball', slug: 'alexander-superball', cc: 43, sysex: 0 },
			{ name: 'Syntax Error', slug: 'alexander-syntax-error', cc: 21, sysex: 0 },
			{ name: 'Wavelength', slug: 'alexander-wavelength', cc: 18, sysex: 0 },
		],
	},
	{
		name: 'Animal Factory Amplification',
		slug: 'animal-factory-amplification',
		devices: [
			{
				name: 'Dirty Mirror',
				slug: 'animal-factory-amplification-dirty-mirror',
				cc: 10,
				sysex: 0,
			},
			{
				name: 'Godeater+',
				slug: 'animal-factory-amplification-godeater',
				cc: 7,
				sysex: 0,
			},
			{
				name: 'Ozymandias',
				slug: 'animal-factory-amplification-ozymandias',
				cc: 3,
				sysex: 0,
			},
		],
	},
	{
		name: 'ART',
		slug: 'art',
		devices: [{ name: 'SGX Nightbass', slug: 'art-sgx-nightbass', cc: 0, sysex: 2 }],
	},
	{
		name: 'Ashdown',
		slug: 'ashdown',
		devices: [
			{ name: 'LB 30 2.N MIDI', slug: 'ashdown-lb-30-2-n-midi', cc: 63, sysex: 0 },
		],
	},
	{
		name: 'Atomic',
		slug: 'atomic',
		devices: [
			{ name: 'Amplifire', slug: 'atomic-amplifire', cc: 38, sysex: 0 },
			{ name: 'Amplifire 3', slug: 'atomic-amplifire-3', cc: 50, sysex: 0 },
			{ name: 'Amplifire 6', slug: 'atomic-amplifire-6', cc: 50, sysex: 0 },
			{ name: 'Amplifire 12', slug: 'atomic-amplifire-12', cc: 59, sysex: 0 },
		],
	},
	{
		name: 'Audio Sprokets',
		slug: 'audio-sprokets',
		devices: [
			{ name: 'Tone Dexter II', slug: 'audio-sprokets-tone-dexter-ii', cc: 1, sysex: 0 },
		],
	},
	{
		name: 'AVID',
		slug: 'avid',
		devices: [{ name: 'Eleven Rack', slug: 'avid-eleven-rack', cc: 25, sysex: 0 }],
	},
	{
		name: 'Beetronics',
		slug: 'beetronics',
		devices: [
			{
				name: 'Seabee Harmochorus',
				slug: 'beetronics-seabee-harmochorus',
				cc: 24,
				sysex: 0,
			},
		],
	},
	{
		name: 'Behringer',
		slug: 'behringer',
		devices: [
			{ name: 'Vintager AC112', slug: 'behringer-vintager-ac112', cc: 8, sysex: 0 },
		],
	},
	{
		name: 'Blackstar Amplification',
		slug: 'blackstar-amplification',
		devices: [
			{ name: 'Amped 1', slug: 'blackstar-amplification-amped-1', cc: 25, sysex: 0 },
			{ name: 'Amped 3', slug: 'blackstar-amplification-amped-3', cc: 36, sysex: 0 },
			{
				name: 'Silverline Deluxe',
				slug: 'blackstar-amplification-silverline-deluxe',
				cc: 52,
				sysex: 0,
			},
			{
				name: 'Silverline Deluxe Head',
				slug: 'blackstar-amplification-silverline-deluxe-head',
				cc: 52,
				sysex: 0,
			},
			{
				name: 'Silverline Stereo Deluxe',
				slug: 'blackstar-amplification-silverline-stereo-deluxe',
				cc: 52,
				sysex: 0,
			},
		],
	},
	{
		name: 'BluGuitar',
		slug: 'bluguitar',
		devices: [
			{
				name: 'Amp1 (Mercury & Iridium Edition)',
				slug: 'bluguitar-amp1-mercury-and-iridium-edition',
				cc: 6,
				sysex: 0,
			},
		],
	},
	{
		name: 'Bondi Effects',
		slug: 'bondi-effects',
		devices: [
			{ name: 'Art Van Delay', slug: 'bondi-effects-art-van-delay', cc: 13, sysex: 0 },
		],
	},
	{
		name: 'Boss',
		slug: 'boss',
		devices: [
			{ name: 'DD-200', slug: 'boss-dd-200', cc: 12, sysex: 0 },
			{ name: 'DD-500', slug: 'boss-dd-500', cc: 36, sysex: 19 },
			{ name: 'DM-101', slug: 'boss-dm-101', cc: 8, sysex: 0 },
			{ name: 'GM-800', slug: 'boss-gm-800', cc: 83, sysex: 0 },
			{ name: 'GT-001', slug: 'boss-gt-001', cc: 0, sysex: 14 },
			{ name: 'GT-100', slug: 'boss-gt-100', cc: 0, sysex: 51 },
			{ name: 'GT1000 Core', slug: 'boss-gt1000-core', cc: 1, sysex: 273 },
			{ name: 'GX-100', slug: 'boss-gx-100', cc: 0, sysex: 13 },
			{ name: 'Katana', slug: 'boss-katana', cc: 7, sysex: 0 },
			{ name: 'Katana Artist', slug: 'boss-katana-artist', cc: 13, sysex: 0 },
			{ name: 'Katana Gen 1', slug: 'boss-katana-gen-1', cc: 0, sysex: 445 },
			{ name: 'Katana Head', slug: 'boss-katana-head', cc: 13, sysex: 0 },
			{ name: 'MD-200', slug: 'boss-md-200', cc: 12, sysex: 0 },
			{ name: 'MD-500', slug: 'boss-md-500', cc: 16, sysex: 18 },
			{ name: 'OD-200', slug: 'boss-od-200', cc: 14, sysex: 0 },
			{ name: 'RC-10R', slug: 'boss-rc-10r', cc: 27, sysex: 0 },
			{ name: 'RE-202', slug: 'boss-re-202', cc: 14, sysex: 0 },
			{ name: 'RV-200', slug: 'boss-rv-200', cc: 12, sysex: 0 },
			{ name: 'RV-500', slug: 'boss-rv-500', cc: 21, sysex: 36 },
			{ name: 'Slicer SL-2', slug: 'boss-slicer-sl-2', cc: 3, sysex: 0 },
			{ name: 'SY-1000', slug: 'boss-sy-1000', cc: 64, sysex: 0 },
		],
	},
	{
		name: 'Chaos Audio',
		slug: 'chaos-audio',
		devices: [{ name: 'Stratus', slug: 'chaos-audio-stratus', cc: 6, sysex: 0 }],
	},
	{
		name: 'Chase Bliss Audio',
		slug: 'chase-bliss-audio',
		devices: [
			{ name: 'Ayahuasca', slug: 'chase-bliss-audio-ayahuasca', cc: 15, sysex: 0 },
			{ name: 'bigtime', slug: 'chase-bliss-audio-bigtime', cc: 48, sysex: 0 },
			{
				name: 'Billy Strings Wombtone',
				slug: 'chase-bliss-audio-billy-strings-wombtone',
				cc: 12,
				sysex: 0,
			},
			{
				name: 'Bliss Factory',
				slug: 'chase-bliss-audio-bliss-factory',
				cc: 20,
				sysex: 0,
			},
			{ name: 'Blooper', slug: 'chase-bliss-audio-blooper', cc: 26, sysex: 0 },
			{ name: 'Brothers', slug: 'chase-bliss-audio-brothers', cc: 22, sysex: 0 },
			{ name: 'Brothers AM', slug: 'chase-bliss-audio-brothers-am', cc: 30, sysex: 0 },
			{ name: 'Clean', slug: 'chase-bliss-audio-clean', cc: 76, sysex: 0 },
			{ name: 'Condor', slug: 'chase-bliss-audio-condor', cc: 22, sysex: 0 },
			{ name: 'CXM 1978', slug: 'chase-bliss-audio-cxm-1978', cc: 20, sysex: 0 },
			{ name: 'Dark World', slug: 'chase-bliss-audio-dark-world', cc: 22, sysex: 0 },
			{
				name: 'Generation Loss',
				slug: 'chase-bliss-audio-generation-loss',
				cc: 13,
				sysex: 0,
			},
			{
				name: 'Generation Loss MKI',
				slug: 'chase-bliss-audio-generation-loss-mki',
				cc: 13,
				sysex: 0,
			},
			{
				name: 'Generation Loss MKII',
				slug: 'chase-bliss-audio-generation-loss-mkii',
				cc: 86,
				sysex: 0,
			},
			{ name: 'Gravitas', slug: 'chase-bliss-audio-gravitas', cc: 19, sysex: 0 },
			{ name: 'Habit', slug: 'chase-bliss-audio-habit', cc: 27, sysex: 0 },
			{ name: 'Lossy', slug: 'chase-bliss-audio-lossy', cc: 43, sysex: 0 },
			{
				name: 'Lost and Found',
				slug: 'chase-bliss-audio-lost-and-found',
				cc: 51,
				sysex: 0,
			},
			{ name: 'Mood', slug: 'chase-bliss-audio-mood', cc: 12, sysex: 0 },
			{ name: 'Mood MkII', slug: 'chase-bliss-audio-mood-mkii', cc: 84, sysex: 0 },
			{ name: 'Onward', slug: 'chase-bliss-audio-onward', cc: 97, sysex: 0 },
			{ name: 'Preamp MkII', slug: 'chase-bliss-audio-preamp-mkii', cc: 14, sysex: 0 },
			{
				name: 'Reverse Mode C',
				slug: 'chase-bliss-audio-reverse-mode-c',
				cc: 109,
				sysex: 0,
			},
			{ name: 'Spectre', slug: 'chase-bliss-audio-spectre', cc: 19, sysex: 0 },
			{ name: 'Thermae', slug: 'chase-bliss-audio-thermae', cc: 28, sysex: 0 },
			{ name: 'Tonal Recall', slug: 'chase-bliss-audio-tonal-recall', cc: 19, sysex: 0 },
			{
				name: 'Warped Vinyl HiFi',
				slug: 'chase-bliss-audio-warped-vinyl-hifi',
				cc: 19,
				sysex: 0,
			},
			{ name: 'Wombtone MKI', slug: 'chase-bliss-audio-wombtone-mki', cc: 19, sysex: 0 },
			{
				name: 'Wombtone MKII',
				slug: 'chase-bliss-audio-wombtone-mkii',
				cc: 19,
				sysex: 0,
			},
		],
	},
	{
		name: 'Colortone Pedals',
		slug: 'colortone-pedals',
		devices: [
			{ name: 'VibroVerb', slug: 'colortone-pedals-vibroverb', cc: 16, sysex: 0 },
			{ name: 'ZeroSum', slug: 'colortone-pedals-zerosum', cc: 16, sysex: 0 },
		],
	},
	{
		name: 'Cornerstone Music Gear',
		slug: 'cornerstone-music-gear',
		devices: [
			{ name: 'Nucleo', slug: 'cornerstone-music-gear-nucleo', cc: 26, sysex: 0 },
		],
	},
	{
		name: 'Darkglass Electronics',
		slug: 'darkglass-electronics',
		devices: [
			{ name: 'anagram', slug: 'darkglass-electronics-anagram', cc: 29, sysex: 0 },
			{
				name: 'Microtubes Infinity',
				slug: 'darkglass-electronics-microtubes-infinity',
				cc: 15,
				sysex: 0,
			},
		],
	},
	{
		name: 'Decibel Eleven',
		slug: 'decibel-eleven',
		devices: [
			{ name: 'Loop Expander', slug: 'decibel-eleven-loop-expander', cc: 8, sysex: 0 },
		],
	},
	{
		name: 'DigiTech',
		slug: 'digitech',
		devices: [
			{ name: 'Bass Whammy', slug: 'digitech-bass-whammy', cc: 1, sysex: 0 },
			{ name: 'GSP1101 / RP / GNX', slug: 'digitech-gsp1101-rp-gnx', cc: 0, sysex: 8 },
			{ name: 'Whammy', slug: 'digitech-whammy', cc: 1, sysex: 0 },
			{ name: 'Whammy DT', slug: 'digitech-whammy-dt', cc: 1, sysex: 0 },
		],
	},
	{
		name: 'Dimehead',
		slug: 'dimehead',
		devices: [{ name: 'NAM', slug: 'dimehead-nam', cc: 48, sysex: 0 }],
	},
	{
		name: 'Disaster Area',
		slug: 'disaster-area',
		devices: [
			{ name: 'DPC Micro', slug: 'disaster-area-dpc-micro', cc: 3, sysex: 0 },
			{ name: 'DPC-8EZ Gen2', slug: 'disaster-area-dpc-8ez-gen2', cc: 19, sysex: 0 },
			{
				name: 'micro.clock Gen4',
				slug: 'disaster-area-micro-clock-gen4',
				cc: 6,
				sysex: 0,
			},
			{
				name: 'NNB Expanse Bridge',
				slug: 'disaster-area-nnb-expanse-bridge',
				cc: 5,
				sysex: 0,
			},
		],
	},
	{
		name: 'Dr Scientist',
		slug: 'dr-scientist',
		devices: [
			{ name: 'Dusk', slug: 'dr-scientist-dusk', cc: 18, sysex: 0 },
			{ name: 'The Atmosphere', slug: 'dr-scientist-the-atmosphere', cc: 55, sysex: 0 },
		],
	},
	{
		name: 'Electro Harmonix',
		slug: 'electro-harmonix',
		devices: [
			{
				name: '8-Step Program',
				slug: 'electro-harmonix-8-step-program',
				cc: 63,
				sysex: 0,
			},
			{
				name: '1440 Stereo Looper',
				slug: 'electro-harmonix-1440-stereo-looper',
				cc: 9,
				sysex: 0,
			},
			{ name: '45000', slug: 'electro-harmonix-45000', cc: 29, sysex: 0 },
			{ name: '95000', slug: 'electro-harmonix-95000', cc: 67, sysex: 0 },
			{ name: 'HOG2', slug: 'electro-harmonix-hog2', cc: 40, sysex: 0 },
			{ name: 'ModRex', slug: 'electro-harmonix-modrex', cc: 22, sysex: 0 },
			{
				name: 'POG3 Polyphonic Octave Generator',
				slug: 'electro-harmonix-pog3-polyphonic-octave-generator',
				cc: 49,
				sysex: 0,
			},
		],
	},
	{
		name: 'Electronic Audio Experiments',
		slug: 'electronic-audio-experiments',
		devices: [
			{
				name: 'Prismatic Wall',
				slug: 'electronic-audio-experiments-prismatic-wall',
				cc: 53,
				sysex: 0,
			},
			{
				name: 'Scattering',
				slug: 'electronic-audio-experiments-scattering',
				cc: 51,
				sysex: 0,
			},
			{
				name: 'Sending v2',
				slug: 'electronic-audio-experiments-sending-v2',
				cc: 20,
				sysex: 0,
			},
			{
				name: 'Sending v2 (Firmware 2.0)',
				slug: 'electronic-audio-experiments-sending-v2-firmware-2-0',
				cc: 56,
				sysex: 0,
			},
		],
	},
	{
		name: 'Elektron',
		slug: 'elektron',
		devices: [
			{ name: 'Analog Drive', slug: 'elektron-analog-drive', cc: 26, sysex: 0 },
			{ name: 'Analog Rytm MKII', slug: 'elektron-analog-rytm-mkii', cc: 92, sysex: 0 },
			{ name: 'Digitakt', slug: 'elektron-digitakt', cc: 73, sysex: 0 },
			{ name: 'Model:Cycles', slug: 'elektron-model-cycles', cc: 29, sysex: 0 },
			{ name: 'Model:Samples', slug: 'elektron-model-samples', cc: 29, sysex: 0 },
			{ name: 'Octatrack MKII', slug: 'elektron-octatrack-mkii', cc: 116, sysex: 0 },
		],
	},
	{
		name: 'EM Custom',
		slug: 'em-custom',
		devices: [
			{
				name: 'AMI Universal Dual TRS',
				slug: 'em-custom-ami-universal-dual-trs',
				cc: 8,
				sysex: 0,
			},
		],
	},
	{
		name: 'Empress',
		slug: 'empress',
		devices: [
			{ name: 'Echo System', slug: 'empress-echo-system', cc: 43, sysex: 0 },
			{ name: 'Phaser', slug: 'empress-phaser', cc: 10, sysex: 0 },
			{ name: 'Reverb', slug: 'empress-reverb', cc: 53, sysex: 0 },
			{ name: 'Tremolo2', slug: 'empress-tremolo2', cc: 27, sysex: 0 },
			{ name: 'ZOIA', slug: 'empress-zoia', cc: 2, sysex: 0 },
		],
	},
	{
		name: 'ENGL',
		slug: 'engl',
		devices: [
			{ name: 'Invader II E642II', slug: 'engl-invader-ii-e642ii', cc: 12, sysex: 0 },
			{
				name: 'Savage 60 Mark II E630II',
				slug: 'engl-savage-60-mark-ii-e630ii',
				cc: 28,
				sysex: 0,
			},
			{
				name: 'Savage 120 Mark II E610II',
				slug: 'engl-savage-120-mark-ii-e610ii',
				cc: 28,
				sysex: 0,
			},
			{
				name: 'Steve Morse Signature E656',
				slug: 'engl-steve-morse-signature-e656',
				cc: 1,
				sysex: 0,
			},
			{
				name: 'Steve Morse Signature E657',
				slug: 'engl-steve-morse-signature-e657',
				cc: 1,
				sysex: 0,
			},
			{
				name: 'Steve Morse Signature E658',
				slug: 'engl-steve-morse-signature-e658',
				cc: 1,
				sysex: 0,
			},
			{
				name: 'Steve Morse Signature E659',
				slug: 'engl-steve-morse-signature-e659',
				cc: 1,
				sysex: 0,
			},
			{
				name: 'Steve Morse Signature E660',
				slug: 'engl-steve-morse-signature-e660',
				cc: 1,
				sysex: 0,
			},
			{
				name: 'Steve Morse Signature E661',
				slug: 'engl-steve-morse-signature-e661',
				cc: 1,
				sysex: 0,
			},
			{
				name: 'Steve Morse Signature E662',
				slug: 'engl-steve-morse-signature-e662',
				cc: 1,
				sysex: 0,
			},
			{
				name: 'Steve Morse Signature E663',
				slug: 'engl-steve-morse-signature-e663',
				cc: 1,
				sysex: 0,
			},
			{
				name: 'Steve Morse Signature E664',
				slug: 'engl-steve-morse-signature-e664',
				cc: 1,
				sysex: 0,
			},
			{
				name: 'Steve Morse Signature E665',
				slug: 'engl-steve-morse-signature-e665',
				cc: 1,
				sysex: 0,
			},
			{
				name: 'Steve Morse Signature E666',
				slug: 'engl-steve-morse-signature-e666',
				cc: 1,
				sysex: 0,
			},
			{
				name: 'Steve Morse Signature E667',
				slug: 'engl-steve-morse-signature-e667',
				cc: 1,
				sysex: 0,
			},
			{
				name: 'Steve Morse Signature E668',
				slug: 'engl-steve-morse-signature-e668',
				cc: 1,
				sysex: 0,
			},
			{
				name: 'Steve Morse Signature E669',
				slug: 'engl-steve-morse-signature-e669',
				cc: 1,
				sysex: 0,
			},
			{
				name: 'Steve Morse Signature E670',
				slug: 'engl-steve-morse-signature-e670',
				cc: 1,
				sysex: 0,
			},
			{
				name: 'Steve Morse Signature E671',
				slug: 'engl-steve-morse-signature-e671',
				cc: 1,
				sysex: 0,
			},
		],
	},
	{
		name: 'Eventide',
		slug: 'eventide',
		devices: [
			{ name: 'Blackhole', slug: 'eventide-blackhole', cc: 17, sysex: 0 },
			{ name: 'Factor Series / H9', slug: 'eventide-factor-series-h9', cc: 0, sysex: 9 },
			{ name: 'H9', slug: 'eventide-h9', cc: 29, sysex: 0 },
			{ name: 'Knifedrop', slug: 'eventide-knifedrop', cc: 11, sysex: 0 },
			{ name: 'Rose', slug: 'eventide-rose', cc: 41, sysex: 0 },
			{ name: 'Tricerachorus', slug: 'eventide-tricerachorus', cc: 18, sysex: 0 },
			{ name: 'Ultratap', slug: 'eventide-ultratap', cc: 16, sysex: 0 },
		],
	},
	{
		name: 'EVH',
		slug: 'evh',
		devices: [
			{ name: '5150III 50S 6L6 HEAD', slug: 'evh-5150iii-50s-6l6-head', cc: 2, sysex: 0 },
			{
				name: '5150III 50W 6L6 1X12 COMBO',
				slug: 'evh-5150iii-50w-6l6-1x12-combo',
				cc: 2,
				sysex: 0,
			},
			{
				name: '5150III 50W 6L6 2X12 COMBO',
				slug: 'evh-5150iii-50w-6l6-2x12-combo',
				cc: 2,
				sysex: 0,
			},
			{ name: '5150III 50W 6L6 HEAD', slug: 'evh-5150iii-50w-6l6-head', cc: 2, sysex: 0 },
			{
				name: '5150III 50W EL34 1X12 COMBO',
				slug: 'evh-5150iii-50w-el34-1x12-combo',
				cc: 2,
				sysex: 0,
			},
			{
				name: '5150III 50W EL34 2X12 COMBO',
				slug: 'evh-5150iii-50w-el34-2x12-combo',
				cc: 2,
				sysex: 0,
			},
			{
				name: '5150III 50W EL34 HEAD',
				slug: 'evh-5150iii-50w-el34-head',
				cc: 2,
				sysex: 0,
			},
			{ name: '5150III 50W HEAD', slug: 'evh-5150iii-50w-head', cc: 2, sysex: 0 },
			{ name: '5150III 100S HEAD', slug: 'evh-5150iii-100s-head', cc: 2, sysex: 0 },
			{ name: '5150III 100W HEAD', slug: 'evh-5150iii-100w-head', cc: 2, sysex: 0 },
			{
				name: '5150IIIS 100W EL34 HEAD',
				slug: 'evh-5150iiis-100w-el34-head',
				cc: 2,
				sysex: 0,
			},
		],
	},
	{
		name: 'Fender',
		slug: 'fender',
		devices: [
			{ name: 'Tone Master Pro', slug: 'fender-tone-master-pro', cc: 18, sysex: 0 },
		],
	},
	{
		name: 'Flower Pedals',
		slug: 'flower-pedals',
		devices: [
			{
				name: 'Dahlia Dual Analog Delay',
				slug: 'flower-pedals-dahlia-dual-analog-delay',
				cc: 35,
				sysex: 0,
			},
			{
				name: 'Hosta Wah Filter',
				slug: 'flower-pedals-hosta-wah-filter',
				cc: 40,
				sysex: 0,
			},
			{ name: 'Sunflower', slug: 'flower-pedals-sunflower', cc: 20, sysex: 0 },
		],
	},
	{
		name: 'Fractal Audio',
		slug: 'fractal-audio',
		devices: [
			{ name: 'Axe-Fx II', slug: 'fractal-audio-axe-fx-ii', cc: 0, sysex: 2 },
			{
				name: 'Axe-Fx III / FM3 / FM9',
				slug: 'fractal-audio-axe-fx-iii-fm3-fm9',
				cc: 0,
				sysex: 31,
			},
		],
	},
	{
		name: 'Fractal Audio Systems',
		slug: 'fractal-audio-systems',
		devices: [
			{ name: 'AX8', slug: 'fractal-audio-systems-ax8', cc: 72, sysex: 0 },
			{ name: 'Axe-Fx II', slug: 'fractal-audio-systems-axe-fx-ii', cc: 114, sysex: 0 },
			{
				name: 'Axe-Fx II XL+',
				slug: 'fractal-audio-systems-axe-fx-ii-xl',
				cc: 114,
				sysex: 0,
			},
			{ name: 'FX8', slug: 'fractal-audio-systems-fx8', cc: 87, sysex: 0 },
		],
	},
	{
		name: 'Free The Tone',
		slug: 'free-the-tone',
		devices: [
			{
				name: 'AS-1R Ambi Space',
				slug: 'free-the-tone-as-1r-ambi-space',
				cc: 12,
				sysex: 0,
			},
			{ name: 'FF-1Y', slug: 'free-the-tone-ff-1y', cc: 43, sysex: 0 },
			{
				name: 'Flight Time FT-2Y',
				slug: 'free-the-tone-flight-time-ft-2y',
				cc: 75,
				sysex: 0,
			},
			{ name: 'PA-1QA', slug: 'free-the-tone-pa-1qa', cc: 13, sysex: 0 },
			{ name: 'PA-1QB', slug: 'free-the-tone-pa-1qb', cc: 13, sysex: 0 },
			{ name: 'PA-1QG', slug: 'free-the-tone-pa-1qg', cc: 13, sysex: 0 },
			{
				name: 'TA-1H Tri Avatar',
				slug: 'free-the-tone-ta-1h-tri-avatar',
				cc: 9,
				sysex: 0,
			},
		],
	},
	{
		name: 'G-LAB',
		slug: 'g-lab',
		devices: [
			{ name: 'BC-1', slug: 'g-lab-bc-1', cc: 13, sysex: 0 },
			{ name: 'MIDI 2 X LOOP-er', slug: 'g-lab-midi-2-x-loop-er', cc: 10, sysex: 0 },
			{ name: 'MIDI 4 X LOOP-er', slug: 'g-lab-midi-4-x-loop-er', cc: 10, sysex: 0 },
			{
				name: 'MIDI WOWEE WAH MWW-1',
				slug: 'g-lab-midi-wowee-wah-mww-1',
				cc: 4,
				sysex: 0,
			},
		],
	},
	{
		name: 'Gamechanger Audio',
		slug: 'gamechanger-audio',
		devices: [
			{ name: 'Bigsby', slug: 'gamechanger-audio-bigsby', cc: 31, sysex: 0 },
			{ name: 'Motor Pedal', slug: 'gamechanger-audio-motor-pedal', cc: 12, sysex: 0 },
		],
	},
	{
		name: 'GFI System',
		slug: 'gfi-system',
		devices: [
			{ name: 'Duophony', slug: 'gfi-system-duophony', cc: 25, sysex: 0 },
			{ name: 'Enieqma', slug: 'gfi-system-enieqma', cc: 191, sysex: 0 },
			{ name: 'Solis Ventus', slug: 'gfi-system-solis-ventus', cc: 76, sysex: 0 },
			{ name: 'Specular Tempus', slug: 'gfi-system-specular-tempus', cc: 15, sysex: 0 },
			{ name: 'Synesthesia', slug: 'gfi-system-synesthesia', cc: 39, sysex: 0 },
		],
	},
	{
		name: 'Headrush',
		slug: 'headrush',
		devices: [
			{ name: 'core', slug: 'headrush-core', cc: 52, sysex: 0 },
			{ name: 'Flex Prime', slug: 'headrush-flex-prime', cc: 42, sysex: 0 },
			{ name: 'Gigboard', slug: 'headrush-gigboard', cc: 26, sysex: 0 },
			{ name: 'Looperboard', slug: 'headrush-looperboard', cc: 86, sysex: 0 },
			{ name: 'MX5', slug: 'headrush-mx5', cc: 25, sysex: 0 },
			{ name: 'Pedalbaord', slug: 'headrush-pedalbaord', cc: 34, sysex: 0 },
			{ name: 'Prime', slug: 'headrush-prime', cc: 103, sysex: 0 },
			{ name: 'VX5', slug: 'headrush-vx5', cc: 3, sysex: 0 },
		],
	},
	{
		name: 'Hologram Electronics',
		slug: 'hologram-electronics',
		devices: [
			{
				name: 'Chroma Console',
				slug: 'hologram-electronics-chroma-console',
				cc: 66,
				sysex: 0,
			},
			{
				name: 'Dream Sequence',
				slug: 'hologram-electronics-dream-sequence',
				cc: 5,
				sysex: 0,
			},
			{ name: 'Microcosm', slug: 'hologram-electronics-microcosm', cc: 35, sysex: 0 },
		],
	},
	{
		name: 'Hotone',
		slug: 'hotone',
		devices: [
			{ name: 'Ampero', slug: 'hotone-ampero', cc: 60, sysex: 0 },
			{ name: 'Ampero 2 Stomp', slug: 'hotone-ampero-2-stomp', cc: 54, sysex: 0 },
		],
	},
	{
		name: 'Hughes & Kettner',
		slug: 'hughes-and-kettner',
		devices: [
			{
				name: 'Grandmeister 36',
				slug: 'hughes-and-kettner-grandmeister-36',
				cc: 25,
				sysex: 0,
			},
			{
				name: 'GrandMeister Deluxe 40',
				slug: 'hughes-and-kettner-grandmeister-deluxe-40',
				cc: 39,
				sysex: 0,
			},
			{ name: 'TriAmp Mk3', slug: 'hughes-and-kettner-triamp-mk3', cc: 23, sysex: 0 },
		],
	},
	{
		name: 'IK Multimedia',
		slug: 'ik-multimedia',
		devices: [
			{ name: 'ToneX', slug: 'ik-multimedia-tonex', cc: 117, sysex: 0 },
			{ name: 'ToneX One+', slug: 'ik-multimedia-tonex-one', cc: 96, sysex: 0 },
		],
	},
	{
		name: 'Jackson Audio',
		slug: 'jackson-audio',
		devices: [
			{ name: 'Asabi', slug: 'jackson-audio-asabi', cc: 5, sysex: 0 },
			{ name: 'Bloom', slug: 'jackson-audio-bloom', cc: 9, sysex: 0 },
			{ name: 'Broken Arrow', slug: 'jackson-audio-broken-arrow', cc: 7, sysex: 0 },
			{ name: 'El Guapo', slug: 'jackson-audio-el-guapo', cc: 16, sysex: 0 },
			{ name: 'Golden Boy', slug: 'jackson-audio-golden-boy', cc: 7, sysex: 0 },
			{ name: 'New Wave', slug: 'jackson-audio-new-wave', cc: 11, sysex: 0 },
			{ name: 'The Optimist', slug: 'jackson-audio-the-optimist', cc: 3, sysex: 0 },
		],
	},
	{
		name: 'Jet Pedals',
		slug: 'jet-pedals',
		devices: [
			{ name: 'Eternity Delay', slug: 'jet-pedals-eternity-delay', cc: 22, sysex: 0 },
			{ name: 'Lamb', slug: 'jet-pedals-lamb', cc: 11, sysex: 0 },
			{
				name: 'Revelation Reverb 2.0',
				slug: 'jet-pedals-revelation-reverb-2-0',
				cc: 15,
				sysex: 0,
			},
		],
	},
	{
		name: 'JMK Music Pedals',
		slug: 'jmk-music-pedals',
		devices: [
			{ name: 'CLOCKstep', slug: 'jmk-music-pedals-clockstep', cc: 23, sysex: 0 },
		],
	},
	{
		name: 'Judahs',
		slug: 'judahs',
		devices: [
			{ name: 'Kedem 4', slug: 'judahs-kedem-4', cc: 15, sysex: 0 },
			{ name: 'Kedem 6', slug: 'judahs-kedem-6', cc: 28, sysex: 0 },
			{ name: 'Koinos PLS 8', slug: 'judahs-koinos-pls-8', cc: 40, sysex: 0 },
		],
	},
	{
		name: 'Keeley Electronics',
		slug: 'keeley-electronics',
		devices: [
			{ name: 'Nocturne', slug: 'keeley-electronics-nocturne', cc: 16, sysex: 0 },
		],
	},
	{
		name: 'Kemper',
		slug: 'kemper',
		devices: [
			{ name: 'Profiler', slug: 'kemper-profiler', cc: 40, sysex: 41 },
			{ name: 'Profiler Player', slug: 'kemper-profiler-player', cc: 25, sysex: 0 },
		],
	},
	{
		name: 'Kernom',
		slug: 'kernom',
		devices: [
			{ name: 'Elipse', slug: 'kernom-elipse', cc: 11, sysex: 0 },
			{ name: 'Moho', slug: 'kernom-moho', cc: 9, sysex: 0 },
			{ name: 'Ridge', slug: 'kernom-ridge', cc: 9, sysex: 0 },
		],
	},
	{
		name: 'Kinotone',
		slug: 'kinotone',
		devices: [
			{ name: 'Ghosts', slug: 'kinotone-ghosts', cc: 27, sysex: 0 },
			{ name: 'Ribbons', slug: 'kinotone-ribbons', cc: 48, sysex: 0 },
			{ name: 'Ribbons (FW v2.0)', slug: 'kinotone-ribbons-fw-v2-0', cc: 60, sysex: 0 },
			{ name: 'Sparks', slug: 'kinotone-sparks', cc: 59, sysex: 0 },
		],
	},
	{
		name: 'Korg',
		slug: 'korg',
		devices: [
			{ name: 'Pandora PX5D', slug: 'korg-pandora-px5d', cc: 0, sysex: 27 },
			{ name: 'volca beats', slug: 'korg-volca-beats', cc: 20, sysex: 0 },
			{ name: 'volca drum', slug: 'korg-volca-drum', cc: 31, sysex: 0 },
			{ name: 'volca kick', slug: 'korg-volca-kick', cc: 10, sysex: 0 },
			{ name: 'volca sample', slug: 'korg-volca-sample', cc: 11, sysex: 0 },
		],
	},
	{
		name: 'KSR Amplification',
		slug: 'ksr-amplification',
		devices: [
			{ name: 'Artemis 100', slug: 'ksr-amplification-artemis-100', cc: 121, sysex: 0 },
			{
				name: 'Colossus H-100',
				slug: 'ksr-amplification-colossus-h-100',
				cc: 121,
				sysex: 0,
			},
			{ name: 'Gemini 100', slug: 'ksr-amplification-gemini-100', cc: 121, sysex: 0 },
		],
	},
	{
		name: 'Laney',
		slug: 'laney',
		devices: [
			{
				name: 'BBC The Difference Engine',
				slug: 'laney-bbc-the-difference-engine',
				cc: 52,
				sysex: 0,
			},
			{
				name: 'Ironheart - Loudpedal',
				slug: 'laney-ironheart-loudpedal',
				cc: 6,
				sysex: 0,
			},
		],
	},
	{
		name: 'Line 6',
		slug: 'line-6',
		devices: [
			{ name: 'Catalyst CX', slug: 'line-6-catalyst-cx', cc: 30, sysex: 0 },
			{ name: 'DL4 MKII', slug: 'line-6-dl4-mkii', cc: 23, sysex: 0 },
			{
				name: 'DL4 MkII Delay Modeler',
				slug: 'line-6-dl4-mkii-delay-modeler',
				cc: 22,
				sysex: 0,
			},
			{ name: 'DT25 112', slug: 'line-6-dt25-112', cc: 203, sysex: 0 },
			{ name: 'DT25 Head', slug: 'line-6-dt25-head', cc: 203, sysex: 0 },
			{ name: 'Helix', slug: 'line-6-helix', cc: 38, sysex: 0 },
			{ name: 'Helix Floor', slug: 'line-6-helix-floor', cc: 39, sysex: 0 },
			{ name: 'Helix LT', slug: 'line-6-helix-lt', cc: 37, sysex: 0 },
			{
				name: 'Helix Rack and Control',
				slug: 'line-6-helix-rack-and-control',
				cc: 38,
				sysex: 0,
			},
			{ name: 'Helix Stadium', slug: 'line-6-helix-stadium', cc: 26, sysex: 0 },
			{ name: 'HX Effects', slug: 'line-6-hx-effects', cc: 28, sysex: 0 },
			{ name: 'HX One', slug: 'line-6-hx-one', cc: 40, sysex: 0 },
			{ name: 'HX Stomp', slug: 'line-6-hx-stomp', cc: 32, sysex: 0 },
			{ name: 'HX Stomp XL', slug: 'line-6-hx-stomp-xl', cc: 34, sysex: 0 },
			{
				name: 'M5 Stompbox Modeler',
				slug: 'line-6-m5-stompbox-modeler',
				cc: 7,
				sysex: 0,
			},
			{
				name: 'M9 v2.0 Stompbox Modeler',
				slug: 'line-6-m9-v2-0-stompbox-modeler',
				cc: 31,
				sysex: 0,
			},
			{ name: 'M13', slug: 'line-6-m13', cc: 0, sysex: 2 },
			{
				name: 'M13 v2.0 Sompbox Modeler',
				slug: 'line-6-m13-v2-0-sompbox-modeler',
				cc: 43,
				sysex: 0,
			},
			{ name: 'Pocket POD', slug: 'line-6-pocket-pod', cc: 108, sysex: 0 },
			{ name: 'POD', slug: 'line-6-pod', cc: 0, sysex: 6 },
			{ name: 'POD GO', slug: 'line-6-pod-go', cc: 30, sysex: 0 },
			{ name: 'POD HD PRO X', slug: 'line-6-pod-hd-pro-x', cc: 28, sysex: 0 },
			{ name: 'POD HD500X', slug: 'line-6-pod-hd500x', cc: 28, sysex: 0 },
			{
				name: 'POD XT / Bass POD XT',
				slug: 'line-6-pod-xt-bass-pod-xt',
				cc: 0,
				sysex: 7,
			},
			{ name: 'Powercab 112 Plus', slug: 'line-6-powercab-112-plus', cc: 59, sysex: 0 },
			{ name: 'Powercab 212 Plus', slug: 'line-6-powercab-212-plus', cc: 114, sysex: 0 },
			{ name: 'Powercab Plus', slug: 'line-6-powercab-plus', cc: 28, sysex: 0 },
		],
	},
	{
		name: 'Marshall',
		slug: 'marshall',
		devices: [{ name: 'DSL40CR', slug: 'marshall-dsl40cr', cc: 6, sysex: 0 }],
	},
	{
		name: 'Matthews Effects',
		slug: 'matthews-effects',
		devices: [
			{ name: 'Botanist', slug: 'matthews-effects-botanist', cc: 6, sysex: 0 },
			{ name: 'thetechnician', slug: 'matthews-effects-thetechnician', cc: 85, sysex: 0 },
		],
	},
	{
		name: 'Meris',
		slug: 'meris',
		devices: [
			{ name: 'Enzo', slug: 'meris-enzo', cc: 25, sysex: 4 },
			{ name: 'Enzo X', slug: 'meris-enzo-x', cc: 71, sysex: 0 },
			{ name: 'Hedra', slug: 'meris-hedra', cc: 27, sysex: 0 },
			{ name: 'LVX', slug: 'meris-lvx', cc: 115, sysex: 0 },
			{ name: 'Mercury 7', slug: 'meris-mercury-7', cc: 19, sysex: 4 },
			{ name: 'Mercury X', slug: 'meris-mercury-x', cc: 126, sysex: 0 },
			{ name: 'Ottobit JR', slug: 'meris-ottobit-jr', cc: 22, sysex: 4 },
			{ name: 'Ottobit X', slug: 'meris-ottobit-x', cc: 78, sysex: 0 },
			{ name: 'Polymoon', slug: 'meris-polymoon', cc: 25, sysex: 4 },
		],
	},
	{
		name: 'MFB',
		slug: 'mfb',
		devices: [{ name: 'Tanzbar Lite', slug: 'mfb-tanzbar-lite', cc: 25, sysex: 0 }],
	},
	{
		name: 'Montreal Assembly',
		slug: 'montreal-assembly',
		devices: [
			{
				name: '856 for ZELLERSASN',
				slug: 'montreal-assembly-856-for-zellersasn',
				cc: 67,
				sysex: 0,
			},
		],
	},
	{
		name: 'Mooer',
		slug: 'mooer',
		devices: [
			{ name: 'GE300', slug: 'mooer-ge300', cc: 39, sysex: 0 },
			{ name: 'GE300 LITE', slug: 'mooer-ge300-lite', cc: 35, sysex: 0 },
			{ name: 'Ocean Machine', slug: 'mooer-ocean-machine', cc: 88, sysex: 0 },
		],
	},
	{
		name: 'Moog',
		slug: 'moog',
		devices: [
			{ name: 'MF-104M', slug: 'moog-mf-104m', cc: 29, sysex: 0 },
			{ name: 'MF-105M MIDI MuRF', slug: 'moog-mf-105m-midi-murf', cc: 42, sysex: 0 },
			{
				name: 'MF-108M Cluster Flux',
				slug: 'moog-mf-108m-cluster-flux',
				cc: 65,
				sysex: 0,
			},
		],
	},
	{
		name: 'Morningstar Engineering',
		slug: 'morningstar-engineering',
		devices: [
			{ name: 'MC3', slug: 'morningstar-engineering-mc3', cc: 19, sysex: 0 },
			{ name: 'MC6 MKII', slug: 'morningstar-engineering-mc6-mkii', cc: 26, sysex: 0 },
			{ name: 'MC6 PRO', slug: 'morningstar-engineering-mc6-pro', cc: 40, sysex: 0 },
			{ name: 'MC8', slug: 'morningstar-engineering-mc8', cc: 38, sysex: 0 },
			{ name: 'MC8 PRO', slug: 'morningstar-engineering-mc8-pro', cc: 45, sysex: 0 },
			{ name: 'ML5', slug: 'morningstar-engineering-ml5', cc: 17, sysex: 0 },
			{ name: 'ML5R', slug: 'morningstar-engineering-ml5r', cc: 24, sysex: 0 },
			{ name: 'ML10X', slug: 'morningstar-engineering-ml10x', cc: 42, sysex: 0 },
		],
	},
	{
		name: 'Native Audio',
		slug: 'native-audio',
		devices: [
			{ name: 'In the Water', slug: 'native-audio-in-the-water', cc: 17, sysex: 0 },
		],
	},
	{
		name: 'Neunaber',
		slug: 'neunaber',
		devices: [
			{ name: 'Illumine', slug: 'neunaber-illumine', cc: 24, sysex: 0 },
			{ name: 'Neuron', slug: 'neunaber-neuron', cc: 14, sysex: 0 },
		],
	},
	{
		name: 'Neural DSP',
		slug: 'neural-dsp',
		devices: [
			{ name: 'Nano Cortex', slug: 'neural-dsp-nano-cortex', cc: 11, sysex: 0 },
			{ name: 'Quad Cortex', slug: 'neural-dsp-quad-cortex', cc: 44, sysex: 0 },
			{ name: 'Quad Cortex Mini', slug: 'neural-dsp-quad-cortex-mini', cc: 68, sysex: 0 },
		],
	},
	{
		name: 'NUX',
		slug: 'nux',
		devices: [
			{ name: 'cerberus', slug: 'nux-cerberus', cc: 33, sysex: 0 },
			{ name: 'loopcorestereo', slug: 'nux-loopcorestereo', cc: 34, sysex: 0 },
			{ name: 'Tape Echo NDD-7', slug: 'nux-tape-echo-ndd-7', cc: 16, sysex: 0 },
		],
	},
	{
		name: 'Old Blood Noise Endeavors',
		slug: 'old-blood-noise-endeavors',
		devices: [
			{ name: 'Bathing', slug: 'old-blood-noise-endeavors-bathing', cc: 19, sysex: 0 },
			{
				name: 'Black Fountain',
				slug: 'old-blood-noise-endeavors-black-fountain',
				cc: 20,
				sysex: 0,
			},
			{
				name: 'Dark Star Stereo Soundscape Reverb',
				slug: 'old-blood-noise-endeavors-dark-star-stereo-soundscape-reverb',
				cc: 18,
				sysex: 0,
			},
			{ name: 'parting', slug: 'old-blood-noise-endeavors-parting', cc: 27, sysex: 0 },
			{ name: 'Setback', slug: 'old-blood-noise-endeavors-setback', cc: 25, sysex: 0 },
			{
				name: 'Signal Blender Stereo',
				slug: 'old-blood-noise-endeavors-signal-blender-stereo',
				cc: 14,
				sysex: 0,
			},
			{
				name: 'Sunlight Stereo',
				slug: 'old-blood-noise-endeavors-sunlight-stereo',
				cc: 25,
				sysex: 0,
			},
		],
	},
	{
		name: 'One Control',
		slug: 'one-control',
		devices: [
			{
				name: 'Minimal Series MIDI DS Loop',
				slug: 'one-control-minimal-series-midi-ds-loop',
				cc: 6,
				sysex: 0,
			},
			{
				name: 'Minimal Series MIDI Solo Stereo Loop',
				slug: 'one-control-minimal-series-midi-solo-stereo-loop',
				cc: 2,
				sysex: 0,
			},
		],
	},
	{
		name: 'OPFXS',
		slug: 'opfxs',
		devices: [
			{ name: 'Dig Deep MK3', slug: 'opfxs-dig-deep-mk3', cc: 22, sysex: 0 },
			{ name: 'V-Uno', slug: 'opfxs-v-uno', cc: 24, sysex: 0 },
		],
	},
	{
		name: 'Oscillator Devices',
		slug: 'oscillator-devices',
		devices: [
			{ name: 'ABY', slug: 'oscillator-devices-aby', cc: 4, sysex: 0 },
			{
				name: 'Goblin-EXP in EHX Pitch Fork',
				slug: 'oscillator-devices-goblin-exp-in-ehx-pitch-fork',
				cc: 12,
				sysex: 0,
			},
			{ name: 'Hydra 4x', slug: 'oscillator-devices-hydra-4x', cc: 13, sysex: 0 },
			{ name: 'Minerva', slug: 'oscillator-devices-minerva', cc: 12, sysex: 0 },
			{ name: 'Siren', slug: 'oscillator-devices-siren', cc: 43, sysex: 0 },
		],
	},
	{
		name: 'Panda Audio',
		slug: 'panda-audio',
		devices: [
			{ name: 'Future Impact', slug: 'panda-audio-future-impact', cc: 16, sysex: 0 },
		],
	},
	{
		name: 'Peavey',
		slug: 'peavey',
		devices: [{ name: 'Mini MEGA', slug: 'peavey-mini-mega', cc: 11, sysex: 0 }],
	},
	{
		name: 'Pigtronix',
		slug: 'pigtronix',
		devices: [
			{
				name: 'Echolution 2 Deluxe',
				slug: 'pigtronix-echolution-2-deluxe',
				cc: 27,
				sysex: 0,
			},
			{ name: 'Echolution 3', slug: 'pigtronix-echolution-3', cc: 17, sysex: 0 },
			{ name: 'Infinity', slug: 'pigtronix-infinity', cc: 61, sysex: 0 },
		],
	},
	{
		name: 'Poly Effects',
		slug: 'poly-effects',
		devices: [
			{ name: 'Flat V', slug: 'poly-effects-flat-v', cc: 21, sysex: 0 },
			{ name: 'Kanso', slug: 'poly-effects-kanso', cc: 4, sysex: 0 },
			{ name: 'Verbs', slug: 'poly-effects-verbs', cc: 8, sysex: 0 },
		],
	},
	{
		name: 'Positive Grid',
		slug: 'positive-grid',
		devices: [
			{
				name: 'Bias Distortion Pro',
				slug: 'positive-grid-bias-distortion-pro',
				cc: 15,
				sysex: 0,
			},
			{ name: 'Bias Head', slug: 'positive-grid-bias-head', cc: 26, sysex: 0 },
			{ name: 'Bias Rack', slug: 'positive-grid-bias-rack', cc: 19, sysex: 0 },
		],
	},
	{
		name: 'Ramble FX',
		slug: 'ramble-fx',
		devices: [{ name: 'Kismet', slug: 'ramble-fx-kismet', cc: 12, sysex: 0 }],
	},
	{
		name: 'Red Panda',
		slug: 'red-panda',
		devices: [
			{ name: 'Bitmap 2', slug: 'red-panda-bitmap-2', cc: 20, sysex: 0 },
			{ name: 'Context 2', slug: 'red-panda-context-2', cc: 135, sysex: 0 },
			{ name: 'Particle', slug: 'red-panda-particle', cc: 33, sysex: 0 },
			{ name: 'Particle 2', slug: 'red-panda-particle-2', cc: 36, sysex: 0 },
			{ name: 'Radius', slug: 'red-panda-radius', cc: 28, sysex: 0 },
			{ name: 'Raster', slug: 'red-panda-raster', cc: 17, sysex: 0 },
			{ name: 'Raster 2', slug: 'red-panda-raster-2', cc: 33, sysex: 0 },
			{ name: 'Tensor', slug: 'red-panda-tensor', cc: 18, sysex: 0 },
		],
	},
	{
		name: 'Revv',
		slug: 'revv',
		devices: [
			{ name: 'D20 MIDI', slug: 'revv-d20-midi', cc: 53, sysex: 0 },
			{ name: 'G20 MIDI', slug: 'revv-g20-midi', cc: 9, sysex: 0 },
		],
	},
	{
		name: 'RJM Music',
		slug: 'rjm-music',
		devices: [
			{
				name: 'Mastermind PBC/10',
				slug: 'rjm-music-mastermind-pbc-10',
				cc: 70,
				sysex: 0,
			},
			{ name: 'Mini Amp Gizmo', slug: 'rjm-music-mini-amp-gizmo', cc: 24, sysex: 0 },
			{
				name: 'Mini Effect Gizmo X',
				slug: 'rjm-music-mini-effect-gizmo-x',
				cc: 44,
				sysex: 0,
			},
			{ name: 'Overture', slug: 'rjm-music-overture', cc: 18, sysex: 0 },
		],
	},
	{
		name: 'Rocktron',
		slug: 'rocktron',
		devices: [
			{ name: 'All Access', slug: 'rocktron-all-access', cc: 0, sysex: 1 },
			{ name: 'Intellifex XL', slug: 'rocktron-intellifex-xl', cc: 0, sysex: 2 },
		],
	},
	{
		name: 'Roland',
		slug: 'roland',
		devices: [
			{ name: 'GP-10', slug: 'roland-gp-10', cc: 0, sysex: 7 },
			{ name: 'GR-55', slug: 'roland-gr-55', cc: 0, sysex: 6 },
			{ name: 'SY-1000', slug: 'roland-sy-1000', cc: 0, sysex: 9 },
			{ name: 'TR-08', slug: 'roland-tr-08', cc: 38, sysex: 0 },
			{ name: 'TR-8', slug: 'roland-tr-8', cc: 50, sysex: 0 },
			{ name: 'TR-09', slug: 'roland-tr-09', cc: 39, sysex: 0 },
			{ name: 'VG-99', slug: 'roland-vg-99', cc: 0, sysex: 5 },
		],
	},
	{
		name: 'Seymour Duncan',
		slug: 'seymour-duncan',
		devices: [
			{ name: 'Andromeda', slug: 'seymour-duncan-andromeda', cc: 25, sysex: 0 },
			{ name: 'Dark Sun', slug: 'seymour-duncan-dark-sun', cc: 28, sysex: 0 },
		],
	},
	{
		name: 'Sheeran',
		slug: 'sheeran',
		devices: [
			{ name: 'Looper Plus', slug: 'sheeran-looper-plus', cc: 24, sysex: 0 },
			{ name: 'Looper X', slug: 'sheeran-looper-x', cc: 93, sysex: 0 },
		],
	},
	{
		name: 'Singular Sound',
		slug: 'singular-sound',
		devices: [
			{ name: 'Aeros Looper', slug: 'singular-sound-aeros-looper', cc: 108, sysex: 0 },
			{ name: 'BeatBuddy', slug: 'singular-sound-beatbuddy', cc: 37, sysex: 0 },
		],
	},
	{
		name: 'SONULAB',
		slug: 'sonulab',
		devices: [{ name: 'STOMPSTATION', slug: 'sonulab-stompstation', cc: 10, sysex: 0 }],
	},
	{
		name: 'Sound Sculpture',
		slug: 'sound-sculpture',
		devices: [
			{
				name: 'Switchblade 8F',
				slug: 'sound-sculpture-switchblade-8f',
				cc: 12,
				sysex: 0,
			},
		],
	},
	{
		name: 'Source Audio',
		slug: 'source-audio',
		devices: [
			{ name: 'Artifakt', slug: 'source-audio-artifakt', cc: 94, sysex: 0 },
			{ name: 'Atlas', slug: 'source-audio-atlas', cc: 36, sysex: 0 },
			{ name: 'C4 Synth', slug: 'source-audio-c4-synth', cc: 7, sysex: 0 },
			{ name: 'Collider', slug: 'source-audio-collider', cc: 67, sysex: 0 },
			{ name: 'Encounter', slug: 'source-audio-encounter', cc: 51, sysex: 0 },
			{ name: 'EQ2', slug: 'source-audio-eq2', cc: 261, sysex: 0 },
			{ name: 'gemini', slug: 'source-audio-gemini', cc: 43, sysex: 0 },
			{ name: 'lalady', slug: 'source-audio-lalady', cc: 136, sysex: 0 },
			{ name: 'mercury', slug: 'source-audio-mercury', cc: 24, sysex: 0 },
			{ name: 'Nemesis', slug: 'source-audio-nemesis', cc: 239, sysex: 0 },
			{ name: 'Programmable EQ', slug: 'source-audio-programmable-eq', cc: 15, sysex: 0 },
			{
				name: 'Spectrum Intelligence Filter',
				slug: 'source-audio-spectrum-intelligence-filter',
				cc: 7,
				sysex: 0,
			},
			{ name: 'truespring', slug: 'source-audio-truespring', cc: 26, sysex: 0 },
			{ name: 'Ultrawave', slug: 'source-audio-ultrawave', cc: 66, sysex: 0 },
			{
				name: 'ultrawave(guitar)',
				slug: 'source-audio-ultrawave-guitar',
				cc: 65,
				sysex: 0,
			},
			{ name: 'Ventris', slug: 'source-audio-ventris', cc: 101, sysex: 0 },
		],
	},
	{
		name: 'Stone Deaf',
		slug: 'stone-deaf',
		devices: [
			{ name: 'Syncopy', slug: 'stone-deaf-syncopy', cc: 26, sysex: 0 },
			{ name: 'Tremotron', slug: 'stone-deaf-tremotron', cc: 19, sysex: 0 },
		],
	},
	{
		name: 'Strymon',
		slug: 'strymon',
		devices: [
			{ name: 'Bigsky', slug: 'strymon-bigsky', cc: 68, sysex: 0 },
			{ name: 'Bigsky MX', slug: 'strymon-bigsky-mx', cc: 126, sysex: 0 },
			{ name: 'blueSky', slug: 'strymon-bluesky', cc: 22, sysex: 0 },
			{ name: 'Brig', slug: 'strymon-brig', cc: 15, sysex: 0 },
			{ name: 'Cloudburst', slug: 'strymon-cloudburst', cc: 14, sysex: 0 },
			{ name: 'Compadre', slug: 'strymon-compadre', cc: 12, sysex: 0 },
			{ name: 'Deco', slug: 'strymon-deco', cc: 35, sysex: 0 },
			{ name: 'DIG', slug: 'strymon-dig', cc: 37, sysex: 0 },
			{ name: 'EC-1', slug: 'strymon-ec-1', cc: 18, sysex: 0 },
			{ name: 'El Capistan', slug: 'strymon-el-capistan', cc: 33, sysex: 0 },
			{ name: 'Flint', slug: 'strymon-flint', cc: 37, sysex: 0 },
			{ name: 'Iridium', slug: 'strymon-iridium', cc: 18, sysex: 0 },
			{ name: 'Lex', slug: 'strymon-lex', cc: 31, sysex: 0 },
			{ name: 'Mobius', slug: 'strymon-mobius', cc: 76, sysex: 0 },
			{ name: 'NightSky', slug: 'strymon-nightsky', cc: 57, sysex: 0 },
			{ name: 'Riverside', slug: 'strymon-riverside', cc: 22, sysex: 0 },
			{ name: 'Sunset', slug: 'strymon-sunset', cc: 25, sysex: 0 },
			{ name: 'Timeline', slug: 'strymon-timeline', cc: 65, sysex: 0 },
			{ name: 'Timeline MX', slug: 'strymon-timeline-mx', cc: 57, sysex: 0 },
			{ name: 'Ultraviolet', slug: 'strymon-ultraviolet', cc: 13, sysex: 0 },
			{ name: 'Volante', slug: 'strymon-volante', cc: 52, sysex: 0 },
			{ name: 'Zelzah', slug: 'strymon-zelzah', cc: 41, sysex: 0 },
		],
	},
	{
		name: 'Suhr',
		slug: 'suhr',
		devices: [
			{ name: 'Discovery', slug: 'suhr-discovery', cc: 20, sysex: 0 },
			{ name: 'Hedgehog', slug: 'suhr-hedgehog', cc: 9, sysex: 0 },
			{ name: 'PT-100', slug: 'suhr-pt-100', cc: 15, sysex: 0 },
		],
	},
	{
		name: 'Swindler Effects',
		slug: 'swindler-effects',
		devices: [
			{
				name: 'Red Mountain Tremolo V2',
				slug: 'swindler-effects-red-mountain-tremolo-v2',
				cc: 20,
				sysex: 0,
			},
			{
				name: 'The Gulf Chorus V2',
				slug: 'swindler-effects-the-gulf-chorus-v2',
				cc: 15,
				sysex: 0,
			},
		],
	},
	{
		name: 'Synergy Amps',
		slug: 'synergy-amps',
		devices: [
			{ name: 'SYN2', slug: 'synergy-amps-syn2', cc: 7, sysex: 0 },
			{ name: 'Syn20Ir', slug: 'synergy-amps-syn20ir', cc: 6, sysex: 0 },
		],
	},
	{
		name: 'TC Electronic',
		slug: 'tc-electronic',
		devices: [
			{ name: 'Ditto X4', slug: 'tc-electronic-ditto-x4', cc: 17, sysex: 0 },
			{
				name: 'Flashback 2 X4 Delay',
				slug: 'tc-electronic-flashback-2-x4-delay',
				cc: 14,
				sysex: 0,
			},
			{ name: 'G-Major', slug: 'tc-electronic-g-major', cc: 0, sysex: 2 },
			{ name: 'G-Major 2', slug: 'tc-electronic-g-major-2', cc: 0, sysex: 6 },
			{
				name: 'Hall of Fame 2 X4 Reverb',
				slug: 'tc-electronic-hall-of-fame-2-x4-reverb',
				cc: 8,
				sysex: 0,
			},
			{ name: 'Nova Drive', slug: 'tc-electronic-nova-drive', cc: 15, sysex: 0 },
			{ name: 'Plethora X3', slug: 'tc-electronic-plethora-x3', cc: 45, sysex: 0 },
			{ name: 'Plethora X5', slug: 'tc-electronic-plethora-x5', cc: 69, sysex: 0 },
		],
	},
	{
		name: 'Teenage Engineering',
		slug: 'teenage-engineering',
		devices: [{ name: 'OP-Z', slug: 'teenage-engineering-op-z', cc: 48, sysex: 0 }],
	},
	{
		name: 'Tone King',
		slug: 'tone-king',
		devices: [
			{
				name: 'imperialtritubepreamp',
				slug: 'tone-king-imperialtritubepreamp',
				cc: 23,
				sysex: 0,
			},
		],
	},
	{
		name: 'Two Notes Audio Engineering',
		slug: 'two-notes-audio-engineering',
		devices: [
			{ name: 'Opus', slug: 'two-notes-audio-engineering-opus', cc: 69, sysex: 0 },
			{
				name: 'Torpedo C.A.B. (v4 firmware)',
				slug: 'two-notes-audio-engineering-torpedo-c-a-b-v4-firmware',
				cc: 282,
				sysex: 0,
			},
			{
				name: 'Torpedo Captor X',
				slug: 'two-notes-audio-engineering-torpedo-captor-x',
				cc: 291,
				sysex: 0,
			},
			{
				name: 'Torpedo Live v3 firmware',
				slug: 'two-notes-audio-engineering-torpedo-live-v3-firmware',
				cc: 140,
				sysex: 0,
			},
			{
				name: 'Torpedo Live v4 firmware',
				slug: 'two-notes-audio-engineering-torpedo-live-v4-firmware',
				cc: 282,
				sysex: 0,
			},
			{
				name: 'Torpedo Studio',
				slug: 'two-notes-audio-engineering-torpedo-studio',
				cc: 345,
				sysex: 0,
			},
		],
	},
	{
		name: 'Universal Audio',
		slug: 'universal-audio',
		devices: [
			{ name: 'ANTI', slug: 'universal-audio-anti', cc: 31, sysex: 0 },
			{ name: 'Astra', slug: 'universal-audio-astra', cc: 14, sysex: 0 },
			{ name: 'Del-Verb', slug: 'universal-audio-del-verb', cc: 13, sysex: 0 },
			{ name: 'Dream', slug: 'universal-audio-dream', cc: 19, sysex: 0 },
			{ name: 'Enigmatic', slug: 'universal-audio-enigmatic', cc: 31, sysex: 0 },
			{ name: 'Galaxy', slug: 'universal-audio-galaxy', cc: 17, sysex: 0 },
			{ name: 'Golden', slug: 'universal-audio-golden', cc: 14, sysex: 0 },
			{ name: 'Knuckles', slug: 'universal-audio-knuckles', cc: 32, sysex: 0 },
			{ name: 'Lion', slug: 'universal-audio-lion', cc: 26, sysex: 0 },
			{ name: 'Max', slug: 'universal-audio-max', cc: 24, sysex: 0 },
			{ name: 'OX Stomp', slug: 'universal-audio-ox-stomp', cc: 31, sysex: 0 },
			{ name: 'Ruby', slug: 'universal-audio-ruby', cc: 19, sysex: 0 },
			{ name: 'Starlight', slug: 'universal-audio-starlight', cc: 14, sysex: 0 },
			{ name: 'Woodrow', slug: 'universal-audio-woodrow', cc: 15, sysex: 0 },
		],
	},
	{
		name: 'Valeton',
		slug: 'valeton',
		devices: [
			{ name: 'GP-5', slug: 'valeton-gp-5', cc: 20, sysex: 16 },
			{ name: 'GP-200', slug: 'valeton-gp-200', cc: 53, sysex: 0 },
			{ name: 'GP-200LT', slug: 'valeton-gp-200lt', cc: 48, sysex: 0 },
		],
	},
	{
		name: 'Vongon',
		slug: 'vongon',
		devices: [
			{ name: 'Polyphrase', slug: 'vongon-polyphrase', cc: 14, sysex: 0 },
			{ name: 'replay', slug: 'vongon-replay', cc: 35, sysex: 0 },
			{ name: 'Solarfaze', slug: 'vongon-solarfaze', cc: 1, sysex: 0 },
		],
	},
	{
		name: 'Vox',
		slug: 'vox',
		devices: [
			{
				name: 'Valvetronix VTX (VT20X/40X/100X)',
				slug: 'vox-valvetronix-vtx-vt20x-40x-100x',
				cc: 0,
				sysex: 17,
			},
		],
	},
	{
		name: 'Walrus Audio',
		slug: 'walrus-audio',
		devices: [
			{ name: 'ACS1', slug: 'walrus-audio-acs1', cc: 11, sysex: 0 },
			{ name: 'ACS1 MKII', slug: 'walrus-audio-acs1-mkii', cc: 46, sysex: 0 },
			{ name: 'D1 (New version)', slug: 'walrus-audio-d1-new-version', cc: 21, sysex: 0 },
			{ name: 'D1 (Old version)', slug: 'walrus-audio-d1-old-version', cc: 21, sysex: 0 },
			{ name: 'D1 MKII', slug: 'walrus-audio-d1-mkii', cc: 22, sysex: 0 },
			{ name: 'M1', slug: 'walrus-audio-m1', cc: 40, sysex: 0 },
			{ name: 'M1 MKII', slug: 'walrus-audio-m1-mkii', cc: 26, sysex: 0 },
			{
				name: 'Meraki (Stereo Analog Delay)',
				slug: 'walrus-audio-meraki-stereo-analog-delay',
				cc: 19,
				sysex: 0,
			},
			{ name: 'Qi Etherealizer', slug: 'walrus-audio-qi-etherealizer', cc: 24, sysex: 0 },
			{ name: 'R1', slug: 'walrus-audio-r1', cc: 15, sysex: 0 },
			{ name: 'R1 MKII', slug: 'walrus-audio-r1-mkii', cc: 17, sysex: 0 },
			{ name: 'Xero', slug: 'walrus-audio-xero', cc: 17, sysex: 0 },
		],
	},
	{
		name: 'Wampler Pedals',
		slug: 'wampler-pedals',
		devices: [
			{ name: 'Catacombs', slug: 'wampler-pedals-catacombs', cc: 25, sysex: 0 },
			{ name: 'Metaverse', slug: 'wampler-pedals-metaverse', cc: 24, sysex: 0 },
			{
				name: 'Pantheon Deluxe',
				slug: 'wampler-pedals-pantheon-deluxe',
				cc: 14,
				sysex: 0,
			},
			{ name: 'Terraform', slug: 'wampler-pedals-terraform', cc: 19, sysex: 0 },
		],
	},
	{
		name: 'Yamaha',
		slug: 'yamaha',
		devices: [
			{ name: 'Magicstomp (UB99)', slug: 'yamaha-magicstomp-ub99', cc: 0, sysex: 4 },
			{ name: 'THR10', slug: 'yamaha-thr10', cc: 0, sysex: 13 },
		],
	},
	{
		name: 'Zoom',
		slug: 'zoom',
		devices: [
			{
				name: 'G1 FOUR (G/B FOUR series)',
				slug: 'zoom-g1-four-g-b-four-series',
				cc: 0,
				sysex: 10,
			},
			{
				name: 'MS Plus (MS-50G+ etc.)',
				slug: 'zoom-ms-plus-ms-50g-etc',
				cc: 0,
				sysex: 14,
			},
			{
				name: 'MS-50G / MS-60B / MS-70CDR',
				slug: 'zoom-ms-50g-ms-60b-ms-70cdr',
				cc: 0,
				sysex: 6,
			},
		],
	},
]
